/**
 * Kom i gang (#288 slice A, #294; reference docs/design/prototypes/onboarding-handoff-2026-11-02,
 * screen 02): the work cycle Opprett → Kontroller → Rapporter og lønn as the company's own steps,
 * each decided by a saved fact on the server (GET /onboarding), never by a page visited or a tip
 * closed (ONB-03). One next action on top, the phases with «x av y» under it, a row per step that
 * opens the page where the work is done. Pausing the guide is this person's preference and changes
 * no step. The first working day is chosen here (ONB-02); Kontroll waits for it (ONB-12).
 */
import { errorText } from '@/api/client';
import { useWorkspaceWrite } from '@/api/company';
import {
  type Onboarding,
  type Phase,
  type Step,
  type StepKey,
  useGuidePause,
  useOnboarding,
} from '@/api/onboarding';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, LoadError, Skeleton, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { dayMonth } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import s from './komigang.module.css';

const PHASES: { key: Phase; title: string; icon: IconName }[] = [
  { key: 'opprett', title: 'Opprett', icon: 'calendar' },
  { key: 'kontroller', title: 'Kontroller', icon: 'check' },
  { key: 'rapporter', title: 'Rapporter og lønn', icon: 'document' },
];

interface Copy {
  title: string;
  icon: IconName;
  /** where the work is done; absent for the start date, which is chosen on this page */
  to?: string;
  /** a second way to finish the step, under the main action */
  alt?: { to: string; label: string };
  open: (o: Onboarding, n: number) => string;
  done: (o: Onboarding) => string;
}
/** what each step says, open and done, in the manager's words */
const COPY: Record<StepKey, Copy> = {
  arbeidsmate: {
    title: 'Velg arbeidsmåte og startdato',
    icon: 'calendar',
    open: () => T('Når starter dere med Timefly? Kontroll venter til denne dagen.'),
    done: (o) =>
      T('{mode} · Første arbeidsdag {date}', {
        mode: o.mode === 'rota' ? T('Vaktplan og timer') : T('Bare timer'),
        date: o.start_date ? dayMonth(o.start_date) : '',
      }),
  },
  ansatte: {
    title: 'Legg til ansatte',
    icon: 'userPlus',
    to: '/ansatte/importer',
    open: () => T('Hent dem fra lønnssystemet eller legg dem inn selv.'),
    done: (o) => T('{n} ansatte lagt til.', { n: o.facts.people }),
  },
  arbeidstid: {
    title: 'Klargjør ansatte',
    icon: 'users',
    to: '/ansatte?oppgave=arbeidstid',
    open: (_, n) =>
      n === 1 ? T('1 mangler avtalt arbeidstid.') : T('{n} mangler avtalt arbeidstid.', { n }),
    done: () => T('Alle har avtalt arbeidstid.'),
  },
  vaktplan: {
    title: 'Lag og publiser første vaktplan',
    icon: 'calendar',
    to: '/vaktplan',
    open: () => T('Lag et utkast, se over og publiser. De ansatte ser planen først når den er publisert.'),
    done: (o) => T('{n} vakter publisert.', { n: o.facts.published }),
  },
  tilgang: {
    title: 'Gi de ansatte tilgang',
    icon: 'login',
    to: '/ansatte?oppgave=ikke-invitert',
    open: (_, n) => (n === 1 ? T('1 er ikke invitert ennå.') : T('{n} er ikke invitert ennå.', { n })),
    done: (o) => T('Alle er invitert. {n} har logget inn.', { n: o.facts.logged_in }),
  },
  registrert: {
    title: 'Registrer første arbeidsdag',
    icon: 'clock',
    to: '/timeliste',
    open: () => T('Ansatte stempler i Timefly, eller du registrerer start, slutt og pause.'),
    done: (o) => T('{n} registreringer med stempling.', { n: o.facts.worked }),
  },
  godkjent: {
    title: 'Kontroller og godkjenn timer',
    icon: 'check',
    to: '/timeliste?vis=klare',
    open: () => T('Rett mangler, se beregningen og godkjenn. Å rette godkjenner ikke.'),
    done: (o) => T('{n} vakter godkjent.', { n: o.facts.approved }),
  },
  rapport: {
    title: 'Lag første rapport',
    icon: 'download',
    to: '/lonn',
    // report-only (#295 ONB-16): approved hours from Kontroll, no payroll system or pay codes needed
    alt: { to: '/timeliste?rapport=godkjente', label: 'Bare rapport over godkjente timer' },
    open: () =>
      T('Lag lønnsgrunnlag til lønnssystemet, eller bare en rapport over godkjente timer uten lønnsoppsett.'),
    done: (o) => T('{n} filer laget.', { n: o.facts.reports }),
  },
};

export default function KomIGang() {
  const q = useOnboarding();
  const pause = useGuidePause();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError || !q.data)
    return <LoadError what={T('Kom i gang')} detail={errorText(q.error)} onRetry={() => q.refetch()} />;
  const o = q.data;
  const next = o.steps.find((x) => !x.done);
  const paused = !!o.paused_at;
  return (
    <div className={s.page} data-testid="komigang">
      <header className={s.head}>
        <span className={s.mark} aria-hidden="true">
          <Icon name="flag" size={22} />
        </span>
        <div>
          <h1>{T('Kom i gang')}</h1>
          <p>{T('Fra de første ansatte til kontrollert lønnsgrunnlag.')}</p>
        </div>
        <button
          type="button"
          className={s.textBtn}
          data-testid="guide-pause"
          disabled={pause.isPending}
          onClick={() =>
            pause.mutate(!paused, {
              onSuccess: () =>
                toast(paused ? T('Veiledningen er på igjen.') : T('Veiledningen er satt på pause.')),
              onError: (e) => toast(errorText(e)),
            })
          }
        >
          {paused ? T('Fortsett veiledningen') : T('Pause veiledningen')}
        </button>
      </header>

      {paused && (
        <p className={s.note} data-testid="guide-paused">
          {T('Veiledningen er på pause. Stegene under følger fortsatt det dere har lagret.')}
        </p>
      )}

      {next ? <Next o={o} step={next} /> : <AllDone />}

      <ol className={s.phases} aria-label={T('Faser')}>
        {PHASES.map((p) => {
          const list = o.steps.filter((x) => x.phase === p.key);
          const done = list.filter((x) => x.done).length;
          return (
            <li key={p.key} data-testid={`phase-${p.key}`}>
              <span className={`${s.pIcon} ${done === list.length ? s.pDone : ''}`} aria-hidden="true">
                <Icon name={p.icon} size={18} />
              </span>
              <span>
                <b>{T(p.title)}</b>
                <span className={s.meta}>{T('{done} av {all} ferdige', { done, all: list.length })}</span>
                <span className={s.bar} aria-hidden="true">
                  {list.map((x) => (
                    <i key={x.key} className={x.done ? s.on : undefined} />
                  ))}
                </span>
              </span>
            </li>
          );
        })}
      </ol>

      {PHASES.map((p) => (
        <section key={p.key} className={s.section} aria-labelledby={`kig-${p.key}`}>
          <h2 id={`kig-${p.key}`}>{T(p.title)}</h2>
          <ul>
            {o.steps
              .filter((x) => x.phase === p.key)
              .map((x) => (
                <Row key={x.key} o={o} step={x} />
              ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function lineOf(o: Onboarding, step: Step) {
  const c = COPY[step.key];
  return step.done ? c.done(o) : c.open(o, step.count ?? 0);
}

/** The first open step, as the one thing to do now. */
function Next({ o, step }: { o: Onboarding; step: Step }) {
  const c = COPY[step.key];
  return (
    <div className={s.next} data-testid="kig-next">
      <span className={s.nIcon} aria-hidden="true">
        <Icon name={c.icon} size={22} />
      </span>
      <div>
        <h2>{T(c.title)}</h2>
        <p>{lineOf(o, step)}</p>
        {step.key === 'arbeidsmate' ? (
          <StartDate o={o} />
        ) : (
          <Link className="btn pri" to={c.to ?? '/'} data-testid="kig-next-go">
            {T(c.title)}
            <Icon name="arrowRight" size={16} />
          </Link>
        )}
        {c.alt && (
          <Link className={`mlink ${s.alt}`} to={c.alt.to} data-testid="kig-next-alt">
            {T(c.alt.label)}
          </Link>
        )}
      </div>
    </div>
  );
}

function AllDone() {
  return (
    <div className={s.next} data-testid="kig-done">
      <span className={`${s.nIcon} ${s.pDone}`} aria-hidden="true">
        <Icon name="check" size={22} />
      </span>
      <div>
        <h2>{T('Alt er på plass')}</h2>
        <p>
          {T('Dere har gått hele veien fra ansatte til rapport. Siden ligger her om dere vil se den igjen.')}
        </p>
      </div>
    </div>
  );
}

function Row({ o, step }: { o: Onboarding; step: Step }) {
  const c = COPY[step.key];
  return (
    <li className={s.row} data-testid={`step-${step.key}`} data-done={step.done}>
      <span className={`${s.dot} ${step.done ? s.dotDone : ''}`} aria-hidden="true">
        {step.done && <Icon name="check" size={14} strokeWidth={2.4} />}
      </span>
      <span className={s.rText}>
        <b>{T(c.title)}</b>
        <span>{lineOf(o, step)}</span>
        <span className="sr-only">{step.done ? T('Ferdig') : T('Ikke ferdig')}</span>
      </span>
      {c.to && (
        <Link className={s.go} to={c.to}>
          {step.done ? T('Se') : T('Gå til')}
          <Icon name="chevronRight" size={16} />
        </Link>
      )}
    </li>
  );
}

/** The first working day: saved to the workspace with the revision last read (a stale one is refused). */
function StartDate({ o }: { o: Onboarding }) {
  const { tenant } = useTenant();
  const write = useWorkspaceWrite();
  const [date, setDate] = useState(o.start_date ?? '');
  if (!tenant?.me?.is_admin) return <p className={s.meta}>{T('Administratoren velger startdatoen.')}</p>;
  return (
    <form
      className={s.date}
      onSubmit={(e) => {
        e.preventDefault();
        if (!date) return;
        write.mutate(
          { start_date: date, revision: o.revision },
          { onSuccess: () => toast(T('Startdatoen er lagret.')), onError: (err) => toast(errorText(err)) },
        );
      }}
    >
      <label htmlFor="kig-start">
        <span>{T('Første arbeidsdag')}</span>
      </label>
      <DateInput id="kig-start" value={date} onValueChange={setDate} data-testid="kig-start" />
      <Button tone="pri" type="submit" disabled={!date || write.isPending} data-testid="kig-start-save">
        {T('Lagre startdato')}
      </Button>
    </form>
  );
}
